import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner, Row, Col, Badge } from 'react-bootstrap';
import { getEventAssignments, assignEvent, removeEventAssignment, searchStudentsApi } from '../../../api/events';

export const EventStudentsModal = ({ show, onHide, eventId, isOwner }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [formData, setFormData] = useState({ studentEmail: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (formData.studentEmail && formData.studentEmail.length > 2) {
        setSearching(true);
        try {
          const res = await searchStudentsApi({ search: formData.studentEmail });
          setSearchResults(res.data || res.users || []);
        } catch (error) {
          console.error("Failed to search students", error);
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [formData.studentEmail]);

  useEffect(() => {
    if (show && eventId) {
      fetchStudents();
    }
  }, [show, eventId]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      // The API might expect event_id or eventId, and it returns { success, data: [...] }
      const res = await getEventAssignments({ event_id: eventId, limit: 100 });
      
      // Filter for this event just in case the API doesn't filter properly
      const assignments = (res.data || []).filter(a => a.event && a.event._id === eventId);
      
      // Flatten the students out of the assignments
      const flattened = [];
      assignments.forEach(assignment => {
        if (assignment.students && Array.isArray(assignment.students)) {
          assignment.students.forEach(student => {
            flattened.push({
              assignmentId: assignment._id,
              student: student
            });
          });
        }
      });
      
      setStudents(flattened);
    } catch (err) {
      console.warn('Failed to fetch students', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Are you sure you want to remove this student?')) return;
    try {
      await removeEventAssignment(id);
      fetchStudents();
    } catch (err) {
      alert('Failed to remove student.');
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!formData.studentEmail) {
      alert('Please provide student email');
      return;
    }
    try {
      setSubmitting(true);
      
      let userIdToAssign = formData.selectedUserId;
      
      if (!userIdToAssign) {
         const res = await searchStudentsApi({ search: formData.studentEmail });
         const users = res.data || res.users || [];
         const match = users.find(u => u.email === formData.studentEmail);
         if (match) {
            userIdToAssign = match._id;
         } else {
            alert('Student not found with this email.');
            setSubmitting(false);
            return;
         }
      }

      await assignEvent({ eventId: eventId, userIds: [userIdToAssign] });
      setShowAssignForm(false);
      setFormData({ studentEmail: '', selectedUserId: null });
      fetchStudents();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to assign student.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold">Students Enrolled</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        {showAssignForm ? (
          <div className="bg-light p-3 rounded-4 mb-3 border">
            <h6 className="fw-bold mb-3">Assign New Student</h6>
            <Form onSubmit={handleAssignSubmit}>
              <Form.Group className="mb-3 position-relative">
                <Form.Label>Student Email Address</Form.Label>
                <Form.Control 
                  type="email" 
                  placeholder="Enter student email to search..." 
                  value={formData.studentEmail}
                  onChange={e => setFormData({ ...formData, studentEmail: e.target.value, selectedUserId: null })}
                  autoComplete="off"
                  required 
                />
                {formData.studentEmail && formData.studentEmail.length > 2 && searchResults.length > 0 && (
                  <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" style={{ zIndex: 10, maxHeight: '200px', overflowY: 'auto' }}>
                    {searchResults.map((user) => (
                      <div 
                        key={user._id}
                        className="p-2 border-bottom pointer text-dark d-flex align-items-center gap-2" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setFormData({ ...formData, studentEmail: user.email, selectedUserId: user._id });
                          setSearchResults([]);
                        }}
                      >
                        {user.profile_picture ? (
                           <img src={user.profile_picture} alt="Profile" className="rounded-circle object-fit-cover" style={{ width: 30, height: 30 }} />
                        ) : (
                           <i className="bi bi-person-circle text-muted fs-4"></i>
                        )}
                        <div>
                          <div className="fw-bold small">{user.full_name || 'Student'}</div>
                          <div className="text-muted small">{user.email}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {formData.studentEmail && formData.studentEmail.length > 2 && searchResults.length === 0 && !searching && (
                  <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-2 text-center text-muted small" style={{ zIndex: 10 }}>
                    No students found.
                  </div>
                )}
                {searching && (
                  <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-2 text-center text-muted small" style={{ zIndex: 10 }}>
                    Searching...
                  </div>
                )}
              </Form.Group>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="light" className="rounded-pill" onClick={() => setShowAssignForm(false)}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={submitting} className="rounded-pill px-4 cd-btn-orange border-0">
                  {submitting ? 'Assigning...' : 'Assign Student'}
                </Button>
              </div>
            </Form>
          </div>
        ) : (
          <>
            {isOwner && (
              <div className="d-flex justify-content-end mb-3">
                <Button variant="primary" className="rounded-pill px-4 cd-btn-orange border-0" onClick={() => setShowAssignForm(true)}>
                  <i className="bi bi-person-plus-fill me-1"></i> Assign Student
                </Button>
              </div>
            )}
            
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            ) : students.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-people text-muted" style={{ fontSize: '3rem' }}></i>
                <h5 className="mt-3">No Students Enrolled</h5>
                <p className="text-muted">No students have been assigned to this class yet.</p>
              </div>
            ) : (
              <Row className="g-3">
                {students.map((item, index) => (
                  <Col md={6} key={`${item.assignmentId}-${index}`}>
                    <div className="card h-100 border-0 shadow-sm rounded-4 position-relative p-3">
                      {isOwner && (
                        <button 
                          className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-2 rounded-circle shadow-sm"
                          onClick={() => handleRemove(item.assignmentId)}
                          title="Remove Student"
                          style={{ zIndex: 2 }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                      <div className="d-flex align-items-center gap-3">
                        <div className="bg-light rounded-circle d-flex align-items-center justify-content-center overflow-hidden" style={{ width: 50, height: 50 }}>
                          {item.student?.profile_picture ? (
                            <img src={item.student.profile_picture} alt="Profile" className="w-100 h-100 object-fit-cover" />
                          ) : (
                            <i className="bi bi-person-fill text-secondary fs-4"></i>
                          )}
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">
                            {item.student?.full_name || 'Student'}
                          </h6>
                          <small className="text-muted">
                            {item.student?.email || ''}
                          </small>
                          <div className="mt-1">
                            <Badge bg="success" className="rounded-pill">Enrolled</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
